import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Grid3X3, Sparkles, ArrowRight, Settings as Cog } from 'lucide-react';
import { InstallBanner } from '../components/InstallBanner';
import { useI18n } from '../lib/i18n';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { ProgressRing } from '../components/ProgressRing';

export function Dashboard() {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const { data } = useQuery({
    queryKey: ['collection'],
    queryFn: () => api.collection(token!),
    enabled: !!token,
  });
  const { data: matchesData } = useQuery({
    queryKey: ['matches', 'top'],
    queryFn: () => api.matches(token!, { limit: 3 }),
    enabled: !!token,
  });

  const pct = data?.completionPct ?? 0;

  return (
    <div className="space-y-6">
      {/* greeting */}
      <header className="pt-2 flex items-start justify-between gap-3">
        <div>
          <p className="text-haze text-sm">{t('dash.hello')}, {user?.displayName?.split(' ')[0]}</p>
          <h1 className="font-display font-extrabold text-3xl text-balance">
            {t('dash.albumStatus')}{' '}
            <span className="text-gradient">{pct}%</span>.
          </h1>
        </div>
        <Link
          to="/app/settings"
          aria-label="Ajustes"
          className="shrink-0 mt-1 grid place-items-center h-10 w-10 rounded-full glass hover:bg-graphite/40 transition"
        >
          <Cog size={18} className="text-haze" />
        </Link>
      </header>

      {/* hero ring */}
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="glass-strong rounded-3xl p-6 grid place-items-center"
      >
        <ProgressRing pct={pct} label={t('album.complete')} />
        <div className="mt-5 grid w-full grid-cols-3 gap-2 text-center">
          <Stat label={t('dash.have')} value={data?.have ?? 0} accent="text-lime" />
          <Stat label={t('dash.missing')} value={data?.missing ?? 0} accent="text-flame" />
          <Stat label={t('dash.dupes')} value={data?.duplicates ?? 0} accent="text-cyan" />
        </div>
      </motion.div>

      {/* CTA principal: añadir laminitas */}
      <div className="grid gap-3 grid-cols-2">
        <Link to="/app/upload" className="relative overflow-hidden rounded-3xl p-5 glass-strong hover:bg-graphite/50 transition group">
          <Camera className="text-flame" size={24} />
          <div className="mt-3 font-display font-bold text-lg">{t('dash.uploadPhoto')}</div>
          <div className="text-xs text-haze mt-1">{t('dash.uploadPhotoDesc')}</div>
          <ArrowRight size={18} className="absolute bottom-4 right-4 text-flame opacity-0 group-hover:opacity-100 transition" />
          <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-flame/30 blur-2xl group-hover:bg-flame/50 transition" />
        </Link>
        <Link to="/app/manual" className="relative overflow-hidden rounded-3xl p-5 glass-strong hover:bg-graphite/50 transition group">
          <Grid3X3 className="text-lime" size={24} />
          <div className="mt-3 font-display font-bold text-lg">{t('dash.manualEntry')}</div>
          <div className="text-xs text-haze mt-1">{t('dash.manualEntryDesc')}</div>
          <ArrowRight size={18} className="absolute bottom-4 right-4 text-lime opacity-0 group-hover:opacity-100 transition" />
          <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-lime/20 blur-2xl group-hover:bg-lime/40 transition" />
        </Link>
      </div>

      {/* Install PWA banner (cuando aplica) */}
      <InstallBanner />

      {/* Match alert (cuando hay) */}
      {(matchesData?.matches?.length ?? 0) > 0 && (
        <Link
          to="/app/matches"
          className="relative block overflow-hidden rounded-2xl border border-lime/40 bg-lime/10 p-4 shadow-[0_0_30px_-10px_rgba(198,255,94,0.6)] hover:bg-lime/15 transition"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl grid place-items-center bg-lime/20 text-lime border border-lime/40">
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-extrabold text-base text-lime">
                {matchesData!.matches.length} {matchesData!.matches.length === 1 ? t('dash.tradePossible') : t('dash.tradesPossible')}
              </div>
              <div className="text-2xs text-haze line-clamp-1">
                {t('dash.tradesPossibleDesc')}
              </div>
            </div>
            <ArrowRight size={18} className="text-lime shrink-0" />
          </div>
          <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-lime/20 blur-2xl" />
        </Link>
      )}

      {/* top matches preview */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-extrabold text-xl">
            {t('dash.matches')} <Sparkles size={16} className="inline text-cyan -mt-1" />
          </h2>
          <Link to="/app/matches" className="text-sm text-haze flex items-center gap-1">
            {t('dash.viewAll')} <ArrowRight size={14} />
          </Link>
        </div>
        {matchesData?.matches?.length ? (
          <div className="flex gap-3 overflow-x-auto scrollbar-none -mx-4 px-4 pb-2">
            {matchesData.matches.map((m) => (
              <Link
                key={m.user_id}
                to="/app/matches"
                className="shrink-0 w-56 glass-strong rounded-2xl p-4"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-lg text-gradient tabular-nums">
                    {Math.round(m.score * 100)}%
                  </span>
                  {m.city && <span className="chip text-2xs">{m.city}</span>}
                </div>
                <div className="mt-3 font-semibold line-clamp-1">{m.display_name}</div>
                <div className="mt-1 text-2xs text-haze">
                  {t('dash.giveTo')} {m.iCanReceive.length} · {t('dash.giveFrom')} {m.iCanGive.length}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="glass rounded-2xl p-5 text-center text-haze text-sm">
            {t('dash.uploadToFindMatches')}
          </div>
        )}
      </section>

      {/* nudge para repetidas */}
      <Link to="/app/duplicates" className="block">
        <div className="glass rounded-2xl p-4 flex items-center justify-between">
          <div>
            <div className="font-semibold">{t('dash.yourDupes')}</div>
            <div className="text-2xs text-haze">{t('dash.yourDupesDesc')}</div>
          </div>
          <span className="chip-flame">{data?.duplicates ?? 0}</span>
        </div>
      </Link>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="rounded-2xl border border-line/70 bg-ink/40 p-3">
      <div className={`font-display font-extrabold text-2xl ${accent} tabular-nums`}>{value}</div>
      <div className="text-2xs uppercase tracking-wider text-haze mt-0.5">{label}</div>
    </div>
  );
}
